import type { Work } from '~/composables/api/useWorkApi'
import { useGameStateStore } from '~/stores/gameState'

/**
 * デコード操作の共通ロジック (#609 / #611)。
 *
 * - WorkCard と PlayDialog で同じ実装を共有する。
 * - 確認ダイアログ表示 / API 呼び出し / 完了通知 / gameState 再読み込み
 *   をひとまとめに提供する。
 *
 * 完了通知は **既存の Notification snackbar (centering43 layout の
 * `<v-snackbar-queue>`)** に push する形で統合する。決して独自の
 * v-snackbar を持たない。
 *
 * 表示ロジック (button visibility / state) はテンプレート側が担当する。
 */
export function useDecodeAction(work: Ref<Work> | Work) {
  const { decodeWork } = useApi()
  const gameStateStore = useGameStateStore()
  const { reloadGameState } = gameStateStore

  const workRef = computed(() => unref(work))

  const isAlreadyDecoded = ref(workRef.value.alreadyDecoded ?? false)
  const isLoading = ref(false)
  const showConfirm = ref(false)

  // The slug. Prefer `work.slug` (new field); fall back to parsing publicUrl.
  const slug = computed(() => {
    const w = workRef.value
    return w.slug ?? w.publicUrl?.split('/').pop() ?? ''
  })

  const open = () => {
    if (isAlreadyDecoded.value) return
    showConfirm.value = true
  }

  const cancel = () => {
    showConfirm.value = false
  }

  const confirm = async (): Promise<void> => {
    if (isLoading.value) return
    isLoading.value = true
    try {
      const res = await decodeWork(slug.value)
      isAlreadyDecoded.value = true
      // Update local work for downstream readers (best-effort; the Work
      // object itself may be a prop, but mutating its sub-properties is
      // fine for transient UI state).
      workRef.value.alreadyDecoded = true
      gameStateStore.notificationQueue.push(
        res.alreadyDecoded
          ? 'すでにデコード済みです。羽根筆のゆりかごから開けます。'
          : `デコードしました (−${res.mpCharged} MP)。羽根筆のゆりかごから開けます。`,
      )
      // Reflect MP / group changes in the global gameState.
      await reloadGameState()
    }
    catch (err) {
      // Nuxt $fetch は FetchError を throw する (statusCode を持つ)。
      // よくあるエラーをユーザーに分かりやすい日本語に置き換える。
      const status = (err as { statusCode?: number, status?: number })?.statusCode
        ?? (err as { statusCode?: number, status?: number })?.status
      let message: string
      switch (status) {
        case 402:
          message = 'MPが足りません'
          break
        case 403:
          message = 'この作品はデコードできません'
          break
        case 404:
          message = '作品が見つかりません'
          break
        default:
          message = err instanceof Error ? err.message : '不明なエラー'
      }
      gameStateStore.notificationQueue.push(`デコードできませんでした: ${message}`)
    }
    finally {
      isLoading.value = false
      showConfirm.value = false
    }
  }

  return {
    isAlreadyDecoded,
    isLoading,
    showConfirm,
    slug,
    open,
    cancel,
    confirm,
  }
}
